import { useEffect } from 'react'

import { addDays, format, startOfWeek } from 'date-fns'
import { es } from 'date-fns/locale'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { Loader2 } from 'lucide-react'

import { firestore } from '@/shared/firebase/firebase.ts'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { HotelReservation, Reservation } from '@monorepo/functions/src/types/reservations'

import { useCalendarStore } from '../model/store'
import { DayColumn } from './DayColumn'

export function CalendarGrid() {
    const { view, selectedDate, setScheduledTasks, isLoading, setIsLoading } = useCalendarStore()

    const weekDays = Array.from({ length: 6 }, (_, i) => {
        const date = addDays(startOfWeek(selectedDate, { locale: es, weekStartsOn: 1 }), i)
        return {
            date: format(date, 'yyyy-MM-dd'),
            dayName: format(date, 'EEEE', { locale: es }),
            dayNumber: format(date, 'd'),
        }
    })

    useEffect(() => {
        const fetchTasks = async () => {
            setIsLoading(true)
            try {
                const startDay = view === 'day' ? format(selectedDate, 'yyyy-MM-dd') : weekDays[0].date
                const endDay = view === 'day' ? format(selectedDate, 'yyyy-MM-dd') : weekDays[weekDays.length - 1].date
                const reservationsRef = collection(firestore, 'reservations')
                // Query for peluqueria reservations
                const peluqueriaQuery = query(
                    reservationsRef,
                    where('type', '==', 'peluqueria'),
                    where('status', '!=', 'cancelled'),
                    where('date', '>=', startDay),
                    where('date', '<=', endDay),
                )
                const peluqueriaSnap = await getDocs(peluqueriaQuery)

                // Query for hotel reservations
                const hotelQuery = query(
                    reservationsRef,
                    where('type', '==', 'hotel'),
                    where('status', '!=', 'cancelled'),
                    where('checkInDate', '<=', endDay),
                    where('checkOutDate', '>=', startDay),
                )
                const hotelSnap = await getDocs(hotelQuery)

                // Merge and extract tasks
                const allReservations: Reservation[] = [
                    ...peluqueriaSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as Reservation),
                    ...hotelSnap.docs
                        .map(doc => ({ ...doc.data(), id: doc.id }) as HotelReservation)
                        .filter(r => r.hairSalonReservationStatus != 'not-requested'),
                ]
                const allTasks = allReservations.flatMap(r => r.tasks || [])
                setScheduledTasks(allTasks)
            } catch (error) {
                console.error('Error fetching tasks:', error)
            } finally {
                setIsLoading(false)
            }
        }

        fetchTasks()
    }, [selectedDate, view])

    return (
        <Card className='flex-1'>
            <CardHeader>
                <div className='flex items-center justify-between'>
                    <CardTitle className='flex items-center gap-2'>
                        {view === 'day'
                            ? format(selectedDate, "EEEE d 'de' MMMM", { locale: es })
                            : `Semana del ${format(new Date(weekDays[0].date), "d 'de' MMMM", {
                                  locale: es,
                              })}`}
                        {isLoading && <Loader2 className='size-10 animate-spin text-primary' />}
                    </CardTitle>
                </div>
            </CardHeader>
            <CardContent>
                <div className='relative'>
                    {view === 'day' ? (
                        <DayColumn
                            date={format(selectedDate, 'yyyy-MM-dd')}
                            dayName={format(selectedDate, 'EEEE', { locale: es })}
                            dayNumber={format(selectedDate, 'd')}
                            isWeekView={false}
                            showHourOnly={true}
                        />
                    ) : (
                        <div className='grid grid-cols-6'>
                            {weekDays.map((day, idx) => (
                                <DayColumn
                                    key={day.date}
                                    date={day.date}
                                    dayName={day.dayName}
                                    dayNumber={day.dayNumber}
                                    isWeekView={true}
                                    showHourOnly={idx === 0}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
